import { useCallback } from 'react';
import { CoordinateMapper } from '../services/CoordinateMapper.js';
import { LayoutCalculator } from '../services/LayoutCalculator.js';
import { SelectionManager } from '../services/SelectionManager.js';
import { calculateRect, findNodeUnderCursor, measureNode } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

export const useInteractionHandlers = ({
  canvasRef,
  scrollContainerRef,
  isLiveMode,
  isSpacePressed,
  canvasScale,
  activeNodes,
  rootW,
  rootH,
  selectedNodeIds,
  clearSelection,
  panning,
  startSelectionBox,
  dragging,
  resizing,
  saveInteractionState,
  activeBreakpoint = 'default'
}) => {
  const handleFocusItem = useCallback((node) => {
    // Note: LayoutCalculator.measureNodeById currently doesn't accept activeBreakpoint.
    // It calls measureNode which DOES. We should probably update LayoutCalculator too,
    // but for now let's just assume focus works on base or we update measureNodeById signature.
    // Let's rely on updated measureNode in utils.js if we pass the arg.
    const rect = measureNode(node.id, activeNodes, rootW, rootH, activeBreakpoint);
    CoordinateMapper.focusNode(rect, scrollContainerRef, canvasRef, canvasScale, rootW, rootH);
  }, [activeNodes, rootW, rootH, scrollContainerRef, canvasRef, canvasScale, activeBreakpoint]);

  const handleCanvasMouseDown = useCallback((e) => {
    if (e.button !== 0 || !canvasRef.current || isLiveMode) return;

    if (isSpacePressed) {
      panning.startPan(e);
      return;
    }

    const coords = CoordinateMapper.screenToCanvas(e.clientX, e.clientY, canvasRef, canvasScale);
    // findNodeUnderCursor might need activeBreakpoint if nodes are hidden/moved?
    // It uses calculateRect internally.
    // For now, let's keep it simple. Selecting something "hidden" but physically present in DOM is weird.
    // But since hidden items are removed from DOM in live mode, and ghosted in design mode, they are selectable.
    const contextId = findNodeUnderCursor({
      nodes: activeNodes,
      x: coords.x,
      y: coords.y,
      rootW,
      rootH,
      activeBreakpoint
    });

    startSelectionBox({
      startX: coords.x,
      startY: coords.y,
      currentX: coords.x,
      currentY: coords.y,
      contextId,
      initialSelection: e.shiftKey ? [...selectedNodeIds] : []
    });

    if (!e.shiftKey) {
      clearSelection();
    }
  }, [
    canvasRef,
    isLiveMode,
    isSpacePressed,
    canvasScale,
    activeNodes,
    rootW,
    rootH,
    selectedNodeIds,
    clearSelection,
    panning,
    startSelectionBox
  ]);

  const handleNodeMouseDown = useCallback((e, id, axis = null, setSelectedNodeIds) => {
    if (isLiveMode || isSpacePressed || e.ctrlKey || e.metaKey) return;

    e.stopPropagation();
    // e.preventDefault(); // Removed to allow standard click/dblclick processing

    saveInteractionState(activeNodes, selectedNodeIds);

    const targetId = id;
    const node = activeNodes.find(n => n.id === targetId);
    if (!node) return;

    const resolvedNode = resolveNode(node, activeBreakpoint);

    // Check if disabled - prevent interaction with disabled nodes
    const isEnabled = resolvedNode.props?.enabled !== false;
    if (!isEnabled) return;

    // Check visibility (edit mode only) - prevent interaction with hidden nodes
    let isVisible = true;
    if (resolvedNode.props?.visibility !== undefined) {
      if (typeof resolvedNode.props.visibility === 'boolean') {
        isVisible = resolvedNode.props.visibility;
      } else if (activeBreakpoint && typeof resolvedNode.props.visibility === 'object') {
        if (resolvedNode.props.visibility[activeBreakpoint] === false) {
          isVisible = false;
        }
      }
    }
    if (!isVisible) return;

    let newSelection = [...selectedNodeIds];
    const isSelected = selectedNodeIds.includes(targetId);

    if (e.shiftKey) {
      if (newSelection.includes(targetId)) {
        newSelection = newSelection.filter(sid => sid !== targetId);
      } else {
        newSelection.push(targetId);
      }
      setSelectedNodeIds(newSelection);
    } else if (!isSelected) {
      newSelection = [targetId];
      setSelectedNodeIds(newSelection);
    }

    if (node.props.locked) return;

    const initialRects = new Map();
    const nodesToDrag = e.shiftKey ? newSelection : isSelected ? selectedNodeIds : [targetId];

    activeNodes.forEach(n => {
      if (nodesToDrag.includes(n.id) && !n.props.locked) {
        // Resolve Node & Parent for correct layout
        const resolvedNode = resolveNode(n, activeBreakpoint);
        const parent = activeNodes.find(p => p.id === n.parentId);
        let parentW = rootW;
        let parentH = rootH;
        
        if (parent) {
            const resolvedParent = resolveNode(parent, activeBreakpoint);
            const pRect = calculateRect(resolvedParent, rootW, rootH);
            parentW = pRect.width;
            parentH = pRect.height;
        }
        
        initialRects.set(n.id, calculateRect(resolvedNode, parentW, parentH));
      }
    });

    if (initialRects.size > 0) {
      dragging.startDrag(e, nodesToDrag, initialRects, axis);
    }
  }, [
    isLiveMode,
    isSpacePressed,
    activeNodes,
    selectedNodeIds,
    rootW,
    rootH,
    saveInteractionState,
    dragging,
    activeBreakpoint
  ]);

  const handleResizeMouseDown = useCallback((e, id, handle) => {
    e.stopPropagation();
    e.preventDefault();

    const node = activeNodes.find(n => n.id === id);
    if (node && node.props.locked) return;

    saveInteractionState(activeNodes, selectedNodeIds);

    const initialRects = new Map();
    const nodeSnapshots = {};

    selectedNodeIds.forEach(sid => {
      const n = activeNodes.find(node => node.id === sid);
      if (n && !n.props.locked) {
        // Resolve Node & Parent
        const resolvedNode = resolveNode(n, activeBreakpoint);
        const parent = activeNodes.find(p => p.id === n.parentId);
        let parentW = rootW;
        let parentH = rootH;
        
        if (parent) {
            const resolvedParent = resolveNode(parent, activeBreakpoint);
            const pRect = calculateRect(resolvedParent, rootW, rootH);
            parentW = pRect.width;
            parentH = pRect.height;
        }

        initialRects.set(sid, calculateRect(resolvedNode, parentW, parentH));
        nodeSnapshots[sid] = n;
      }
    });

    if (initialRects.size > 0) {
      resizing.startResize(e, id, handle, initialRects, nodeSnapshots);
    }
  }, [activeNodes, selectedNodeIds, rootW, rootH, saveInteractionState, resizing, activeBreakpoint]);

  return {
    handleFocusItem,
    handleCanvasMouseDown,
    handleNodeMouseDown,
    handleResizeMouseDown
  };
};
